import { AfterViewInit, Directive, inject, Input, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { CommonUtils } from '../../common/common-utils';
import { Identifiable } from '../../../shared/models/base/identifiable';
import { UnsavedChangesTrackerService } from '../../services/unsaved-changes-tracker.service';

@Directive()
export abstract class TrackUnsavedFieldBase<T extends Identifiable> implements AfterViewInit, OnDestroy {
  @Input() public item?: T;
  protected abstract readonly formGroup: FormGroup;

  protected readonly formBuilder = inject(FormBuilder);

  protected readonly resetFieldTracking$ = new Subject<unknown>();

  protected previousItemId?: string;

  protected unsavedChangesService = inject(UnsavedChangesTrackerService);

  private readonly unsavedKey: string;
  private readonly destroy$ = new Subject<void>();

  /**
   * Constructor for TrackUnsavedFieldBase.
   * @param unsavedKey The key to use for storing unsaved changes in session storage.
   */
  constructor(unsavedKey: string) {
    this.unsavedKey = unsavedKey;
  }

  public ngAfterViewInit(): void {
    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.storeChangesInSession();
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Gets the current item based on the form values.
   */
  protected abstract getCurrentItemFromFormValues(): T;

  protected changesDetected(): boolean {
    const itemFromValues = this.getCurrentItemFromFormValues();
    return (
      this.previousItemId !== undefined &&
      this.item?.id !== undefined &&
      this.previousItemId === this.item?.id &&
      !CommonUtils.deepEqual(this.item, itemFromValues)
    );
  }

  protected storeChangesInSession(): void {
    if (this.formGroup && this.changesDetected()) {
      this.item = this.getCurrentItemFromFormValues();
      this.unsavedChangesService.saveUnsavedChangesToSessionStorage<T>(this.unsavedKey, this.item!);
    } else {
      this.unsavedChangesService.clearUnsavedChangesFromSessionStorage(this.unsavedKey);
    }
  }
}
